<template>
  <div class="page flex-col">
    <!-- 公共头部 -->
    <PageHeader />

    <!-- 搜索主体：左侧结果 + 右侧热点榜 -->
    <div class="search-main flex-row">
      <!-- 左侧搜索结果 -->
      <div class="search-left flex-col">
        <div class="search-header">
          <span v-if="query" class="search-key">“{{ query }}”的搜索结果</span>
          <span v-if="!loading" class="search-count">共 {{ searchList.length }} 条</span>
        </div>

        <div class="search-list">
          <!-- 加载中显示骨架屏 -->
          <template v-if="loading">
            <div v-for="(index) in 5" :key="index" class="search-item skeleton-item">
              <div class="skeleton-img"></div>
              <div class="skeleton-info">
                <div class="skeleton-title"></div>
                <div class="skeleton-summary"></div>
                <div class="skeleton-meta"></div>
              </div>
            </div>
          </template>

          <!-- 实际搜索结果 -->
          <template v-else>
            <a
              v-for="(item, index) in visibleList"
              :key="index"
              class="search-item flex-row"
              :href="`/zp/a/${item.newsCode}`"
              target="_blank"
            >
              <img
                class="search-img"
                referrerpolicy="no-referrer"
                v-if="item.coverImage"
                :src="item.coverImage || 'https://image.cbrand.com.cn/default/2026/10/AA38368160C540F19F19189B1D3A82AE.png'"
                :alt="item.title"
                  @error="handleCardImageError(index)"
              />
              <div class="search-info flex-col">
                <span class="search-title" v-html="highlight(item.title)"></span>
                <span class="search-summary" v-html="highlight(item.summary)"></span>
                <div class="search-meta flex-row">
                  <span class="meta-source"> {{ getSource(item.source) }}</span>
                  <span class="meta-author">{{ item.author }}</span>
                  <span class="meta-time">{{ formatTime(item.publishTime) }}</span>
                </div>
              </div>
            </a>
          </template>
        </div>

        <!-- 加载更多：默认显示 10 条，点击后显示全部（不再请求接口） -->
        <div
          v-if="visibleCount < searchList.length"
          class="load-more"
          @click="loadMore"
        >
          <span class="load-more-text">点击加载更多</span>
        </div>

        <!-- 空状态 -->
        <div v-if="!loading && searchList.length === 0" class="search-empty">
          <div class="empty-box"></div>
          <span class="empty-text">未找到相关新闻</span>
        </div>
      </div>

      <!-- 右侧热点榜 -->
      <div class="search-right flex-col">
        <HotList />
      </div>
    </div>

    <!-- 公共底部 -->
    <PageFooter />

    <!-- ================= 回到顶部按钮 ================= -->
    <button 
      v-show="showBackToTop" 
      class="back-to-top" 
      @click="scrollToTop"
      title="回到顶部"
      aria-label="回到顶部"
    >
      <!-- 使用你提供的图片链接 -->
      <img 
        src="https://image.cbrand.com.cn/default/2026/10/2D3A47A40740459C98A490CF835B4529.png" 
        alt="回到顶部" 
        class="back-to-top-icon"
      />
    </button>
    <!-- =============================================== -->

  </div>
</template>

<script>
import PageHeader from '../detail/components/PageHeader.vue'
import PageFooter from '../detail/components/PageFooter.vue'
import HotList from '../detail/components/HotList.vue'
import { searchNews } from '../../api'

export default {
  name: 'NewsSearch',
  components: {
    PageHeader,
    PageFooter,
    HotList
  },
  data() {
    return {
      // 当前搜索关键词
      query: '',
      // 搜索结果列表
      searchList: [],
      // 加载状态
      loading: false,
      // 当前已展示条数，默认显示 10 条
      visibleCount: 10,
      
      // 新增：控制回到顶部按钮显示
      showBackToTop: false 
    }
  },
  computed: {
    // 根据当前展示条数截取的搜索结果
    visibleList() {
      return this.searchList.slice(0, this.visibleCount)
    }
  },
  watch: {
    // 监听路由参数变化，重新搜索
    '$route.query.query'(val) {
      this.query = val || ''
      this.visibleCount = 10
      this.fetchSearchList()
    }
  },
  // 新增：监听滚动事件
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  // 新增：组件销毁前移除监听，防止内存泄漏
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  created() {
    this.query = this.$route.query.query || ''
    this.fetchSearchList()
  },
  methods: {
    // 新增：处理滚动事件
    handleScroll() {
      // 当页面垂直滚动超过 300px 时显示按钮
      this.showBackToTop = window.scrollY > 300
    },
    // 新增：平滑回到顶部
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    // 调用搜索接口：固定 pageSize=1000、pageNum=1
    async fetchSearchList() {
      if (!this.query) {
        this.searchList = []
        return
      }
      this.loading = true
      try {
        const list = await searchNews(this.query, 1, 1000)
        this.searchList = list
      } catch (e) {
        console.error('搜索失败', e)
        this.searchList = []
      } finally {
        this.loading = false
      }
    },
     handleCardImageError(index) {
      if (this.searchList[index]) {
        this.searchList[index].coverImage = 'https://image.cbrand.com.cn/default/2026/10/AA38368160C540F19F19189B1D3A82AE.png'
      }
    },
    // 截取字符串（超过 7 个字符后加省略号）
    truncateSevenChars(str) {
      if (!str || str.length <= 10) return str
      return str.substring(0, 10) + '...'
    },
    getSource(source) {
      if (!source) return ''
      const truncated = source.split('|')[0]
      return this.truncateSevenChars(truncated)
    },
    // 加载更多：直接显示全部，不再调用接口
    loadMore() {
      this.visibleCount = this.searchList.length
    },
    // 高亮关键词
    highlight(text) {
      if (!text || !this.query) return text || ''
      const key = this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const reg = new RegExp(`(${key})`, 'gi')
      return String(text).replace(reg, '<span class="highlight">$1</span>')
    },
    // 格式化发布时间
    formatTime(time) {
      if (!time) return ''
      const t = typeof time === 'string' ? new Date(time.replace(/-/g, '/')) : new Date(time)
      if (isNaN(t.getTime())) return time
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.page {
  width: 1400px;
  margin: 0 auto;
  background-color: rgba(255, 255, 255, 1);
  min-height: 100vh;
}

/* 搜索主体 */
.search-main {
  width: 1400px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 35px;
  margin-top: 20px;
  padding-bottom: 60px;
}

/* 左侧结果区 */
.search-left {
  width: 1042px;
  flex-shrink: 0;
}

.search-header {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.search-key {
  font-size: 18px;
  font-family: Microsoft YaHei-Regular;
  color: rgba(51, 51, 51, 1);
  font-weight: 600;
}

.search-count {
  font-size: 14px;
  font-family: Microsoft YaHei-Regular;
  color: rgba(153, 153, 153, 1);
}

/* 搜索结果列表 */
.search-list {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.search-item {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 20px 0;
  border-bottom: 1px solid rgba(240, 240, 240, 1);
  text-decoration: none;
  color: inherit;
}

.search-item:hover .search-title {
  color: rgba(229, 1, 19, 1);
}

.search-img {
  width: 213px;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
  margin-right: 20px;
}

.search-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.search-title {
  font-size: 20px;
  font-family: Microsoft YaHei-Regular;
  color: rgba(0, 0, 0, 1);
  line-height: 28px;
  font-weight: 600;
  margin-bottom: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.search-title >>> .highlight {
  color: rgba(229, 1, 19, 1);
}

.search-summary {
  font-size: 14px;
  font-family: Microsoft YaHei-Regular;
  color: rgba(102, 102, 102, 1);
  line-height: 22px;
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.search-summary >>> .highlight {
  color: rgba(229, 1, 19, 1);
}

.search-meta {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  font-family: Microsoft YaHei-Regular;
  color: rgba(153, 153, 153, 1);
}

/* 加载更多 */
.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 154px;
  height: 34px;
  margin: 40px auto 20px;
  cursor: pointer;
}

.load-more-text {
  font-size: 16px;
  font-family: Microsoft YaHei-Regular;
  color: rgba(102, 102, 102, 1);
  line-height: 28px;
}

/* 右侧热点榜 */
.search-right {
  width: 323px;
  flex-shrink: 0;
}

/* 空状态 */
.search-empty {
  width: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(249, 249, 249, 1);
}

.empty-box {
  width: 88px;
  height: 88px;
  border: 2px dashed rgba(206, 206, 206, 1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  position: relative;
}

.empty-box::before {
  content: '';
  width: 38px;
  height: 30px;
  border: 2px solid rgba(206, 206, 206, 1);
  border-radius: 2px;
}

.empty-box::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 2px;
  background-color: rgba(206, 206, 206, 1);
}

.empty-text {
  font-size: 18px;
  font-family: Microsoft YaHei-Regular;
  color: rgba(153, 153, 153, 1);
  line-height: 28px;
}

/* 骨架屏样式 */
.skeleton-item {
  cursor: default;
  animation: none;
}

.skeleton-img {
  width: 220px;
  height: 140px;
  background: linear-gradient(
    90deg,
    rgba(240, 240, 240, 1) 25%,
    rgba(232, 232, 232, 1) 50%,
    rgba(240, 240, 240, 1) 75%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  flex-shrink: 0;
  margin-right: 20px;
  animation: shimmer 1.5s infinite;
}

.skeleton-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-title {
  height: 24px;
  background: linear-gradient(
    90deg,
    rgba(240, 240, 240, 1) 25%,
    rgba(232, 232, 232, 1) 50%,
    rgba(240, 240, 240, 1) 75%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: shimmer 1.5s infinite;
}

.skeleton-summary {
  height: 66px;
  background: linear-gradient(
    90deg,
    rgba(240, 240, 240, 1) 25%,
    rgba(232, 232, 232, 1) 50%,
    rgba(240, 240, 240, 1) 75%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: shimmer 1.5s 0.2s infinite;
}

.skeleton-meta {
  height: 16px;
  width: 240px;
  background: linear-gradient(
    90deg,
    rgba(240, 240, 240, 1) 25%,
    rgba(232, 232, 232, 1) 50%,
    rgba(240, 240, 240, 1) 75%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: shimmer 1.5s 0.4s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* ================= 回到顶部按钮样式 ================= */
.back-to-top {
  position: fixed;    /* 固定定位，不受页面滚动影响 */
  bottom: 50px;       /* 距离屏幕底部 */
  right: 50px;        /* 距离屏幕右侧 */
  width: 54px;
  height: 54px;
  background-color: transparent; /* 背景透明，图片本身带有圆形 */
  border: none;                  /* 移除边框 */
  border-radius: 50%;            /* 圆形点击区域 */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;                  /* 保证在最上层 */
  padding: 0;
  outline: none;
  transition: all 0.3s ease-in-out;
  /* 添加阴影让按钮更立体 */
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
}

/* 按钮内部图片样式 */
.back-to-top-icon {
  width: 100%;
  height: 100%;
  object-fit: contain; /* 保持图片比例 */
  display: block;
}

/* 鼠标悬停效果 */
.back-to-top:hover {
  transform: scale(1.1); /* 放大 */
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.25)); /* 阴影加深 */
}

/* 点击时的按压效果 */
.back-to-top:active {
  transform: scale(0.95); /* 缩小 */
}
</style>